import { GRAPHQL_PATH } from '../axios/axios.constants';
import { getAxiosInstance } from '../axios/axios.utilities';
import type { AxiosInstance } from 'axios';

export type GraphQLRequestOverrides = {
  /** Override the axios instance for this request (e.g. the instance provided by <ApiProvider>). */
  axiosInstance?: AxiosInstance;
};

/**
 * A GraphQL operation, by name and type. Callers declare one on the request config and observers
 * read the same shape off the lifecycle event.
 */
export type GraphqlOperation = { name: string; type: 'query' | 'mutation' | 'subscription' };

/**
 * An operation the generated GraphQL API declares: the document to send, plus the name and type
 * that label the request.
 */
export type GraphqlDocumentOperation = GraphqlOperation & { document: string };

/**
 * Sends a GraphQL document to the appserver and unwraps the response payload, throwing when the response carries
 * GraphQL errors.
 */
export async function executeGraphQL<TData, TVariables>(
  operation: GraphqlDocumentOperation,
  variables?: TVariables,
  overrides?: GraphQLRequestOverrides,
): Promise<TData> {
  const axiosInstance = overrides?.axiosInstance ?? getAxiosInstance();
  const response = await axiosInstance.request<{ data: TData; errors?: { message: string }[] }>({
    method: 'post',
    url: GRAPHQL_PATH,
    urlTemplate: GRAPHQL_PATH,
    // Only the name and the type go on the config; the document stays out of the lifecycle event.
    graphqlOperation: { name: operation.name, type: operation.type },
    data: { query: operation.document, variables },
  });

  if (response.data.errors?.length) {
    throw new Error(response.data.errors.map((e) => e.message).join('\n'));
  }

  return response.data.data;
}
